import { useState } from 'react';
import useSWR from 'swr';
import apiClient from '@/lib/api.ts';
import { UserPlus, Pencil, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type UserRol = 'admin' | 'user' | 'employee';

interface AdminUserRow {
  id: string;
  tenant_id: string;
  tenant_nombre: string;
  tenant_slug: string;
  nombre: string;
  email: string;
  rol: UserRol;
  activo: boolean;
  employee_id: string | null;
  employee_nombre: string | null;
  employee_apellidos: string | null;
  created_at: string;
}

interface TenantOption {
  id: string;
  nombre_comercial: string;
  dominio_slug: string;
}

interface FormState {
  tenant_id: string;
  nombre: string;
  email: string;
  password: string;
  rol: UserRol;
  activo: boolean;
}

const EMPTY_FORM: FormState = {
  tenant_id: '',
  nombre: '',
  email: '',
  password: '',
  rol: 'user',
  activo: true,
};

// ─── Paleta — tokens CSS del tema global (index.css) ─────────────────────────

const C = {
  bg:      'var(--color-bg)',
  surface: 'var(--color-surface)',
  border:  'var(--color-border)',
  text:    'var(--color-text)',
  muted:   'var(--color-muted)',
  primary: 'var(--color-accent)',
  danger:  'var(--color-danger)',
  success: 'var(--color-success)',
};

const ROL_BADGE: Record<UserRol, { bg: string; color: string; label: string }> = {
  admin:    { bg: '#1e3a5f', color: '#38bdf8', label: 'Admin' },
  user:     { bg: '#1a2a1a', color: '#4ade80', label: 'Usuario' },
  employee: { bg: '#2d1f00', color: '#fbbf24', label: 'Empleado' },
};

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.75rem',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 6, color: C.text,
  fontSize: '0.875rem', outline: 'none',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', }}>
        {label}{required && <span style={{ color: C.danger, marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/** Indicador de fortaleza de contraseña */
function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8)              score++;
  if (password.length >= 12)             score++;
  if (/[A-Z]/.test(password))           score++;
  if (/[0-9]/.test(password))           score++;
  if (/[^A-Za-z0-9]/.test(password))   score++;

  const levels = [
    { label: 'Muy débil', color: '#ef4444' },
    { label: 'Débil',     color: '#f97316' },
    { label: 'Regular',   color: '#eab308' },
    { label: 'Fuerte',    color: '#22c55e' },
    { label: 'Muy fuerte',color: '#16a34a' },
  ];
  const lvl = levels[Math.min(score, 4)];
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
      {levels.map((_l, i) => (
        <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= score - 1 ? lvl.color : C.border, transition: 'background 0.2s' }} />
      ))}
      <span style={{ fontSize: '0.6875rem', color: lvl.color, whiteSpace: 'nowrap', marginLeft: 6, }}>{lvl.label}</span>
    </div>
  );
}

// ─── Modal CRUD ───────────────────────────────────────────────────────────────

function UserModal({
  mode,
  initial,
  tenants,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial: FormState & { id?: string };
  tenants: TenantOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm]       = useState<FormState>(initial);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.tenant_id) { setError('Selecciona un tenant.'); return; }
    if (mode === 'create' && !form.password) { setError('La contraseña es obligatoria al crear.'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        tenant_id: form.tenant_id,
        nombre:    form.nombre,
        email:     form.email,
        rol:       form.rol,
        activo:    form.activo,
      };
      // Solo incluir password si se proporcionó (en edición es opcional)
      if (form.password) payload.password = form.password;

      if (mode === 'create') {
        await apiClient.post('/admin/users', payload);
      } else {
        await apiClient.put(`/admin/users/${initial.id}`, payload);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al guardar. Revisa los datos.');
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 28, width: '100%', maxWidth: 500, }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: C.text }}>
            {mode === 'create' ? 'Nuevo usuario' : 'Editar usuario'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 6, padding: '0.625rem 0.875rem', marginBottom: 16, fontSize: '0.8125rem', color: '#f87171', }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Tenant */}
          <Field label="Tenant" required>
            <select name="tenant_id" value={form.tenant_id} onChange={handleChange} required style={inputStyle} disabled={mode === 'edit'}>
              <option value="">Selecciona un tenant...</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.nombre_comercial} ({t.dominio_slug})</option>
              ))}
            </select>
          </Field>

          {/* Nombre + Email en grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Nombre" required>
              <input name="nombre" value={form.nombre} onChange={handleChange} required style={inputStyle} placeholder="Juan García" />
            </Field>
            <Field label="Email" required>
              <input name="email" type="email" value={form.email} onChange={handleChange} required style={inputStyle} placeholder="juan@empresa.com" />
            </Field>
          </div>

          {/* Contraseña */}
          <Field label={mode === 'create' ? 'Contraseña' : 'Nueva contraseña (dejar vacío para no cambiar)'} required={mode === 'create'}>
            <div style={{ position: 'relative' }}>
              <input
                name="password"
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                style={{ ...inputStyle, paddingRight: '2.5rem' }}
                placeholder={mode === 'edit' ? 'Sin cambios' : 'Mínimo 8 caracteres'}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(p => !p)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center' }}
              >
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <PasswordStrength password={form.password} />
          </Field>

          {/* Rol + Activo */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
            <Field label="Rol" required>
              <select name="rol" value={form.rol} onChange={handleChange} style={inputStyle}>
                <option value="user">Usuario</option>
                <option value="admin">Admin</option>
                <option value="employee">Empleado</option>
              </select>
            </Field>
            <Field label="Estado">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', height: 35 }}>
                <input
                  name="activo"
                  type="checkbox"
                  checked={form.activo}
                  onChange={handleChange}
                  style={{ accentColor: C.primary, width: 16, height: 16 }}
                />
                <span style={{ fontSize: '0.875rem', color: C.text, }}>Activo</span>
              </label>
            </Field>
          </div>

          {/* Acciones */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: '0.875rem', }}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: C.primary, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500, }}
            >
              {saving ? 'Guardando...' : mode === 'create' ? 'Crear usuario' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal confirmar eliminación ──────────────────────────────────────────────

function DeleteModal({ user, onClose, onDeleted }: { user: AdminUserRow; onClose: () => void; onDeleted: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  async function confirm() {
    setLoading(true);
    try {
      await apiClient.delete(`/admin/users/${user.id}`, { data: { tenant_id: user.tenant_id } });
      onDeleted();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al desactivar el usuario.');
      setLoading(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: C.surface, border: `1px solid rgba(220,38,38,0.4)`, borderRadius: 10, padding: 28, width: '100%', maxWidth: 400, }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 10px', fontSize: '1rem', fontWeight: 700, color: C.text }}>Desactivar usuario</h3>
        <p style={{ margin: '0 0 18px', fontSize: '0.875rem', color: C.muted, lineHeight: 1.5 }}>
          ¿Deseas desactivar a <strong style={{ color: C.text }}>{user.nombre}</strong> ({user.email})?
          El usuario no podrá iniciar sesión pero sus datos se conservan.
        </p>
        {error && <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: 14, fontSize: '0.8125rem', color: '#f87171' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: '0.875rem', }}>Cancelar</button>
          <button onClick={confirm} disabled={loading} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: C.danger, color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500, }}>
            {loading ? 'Desactivando...' : 'Desactivar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function AdminUsersPage() {
  const [search,      setSearch]      = useState('');
  const [rolFilt,     setRolFilt]     = useState<'all' | UserRol>('all');
  const [tenantFilt,  setTenantFilt]  = useState<string>('all');
  const [modal,       setModal]       = useState<{ mode: 'create' | 'edit'; user?: AdminUserRow } | null>(null);
  const [toDelete,    setToDelete]    = useState<AdminUserRow | null>(null);

  const { data, isLoading, mutate } = useSWR<{ data: AdminUserRow[] }>(
    '/admin/users',
    (url: string) => apiClient.get(url).then(r => r.data),
    { revalidateOnFocus: false },
  );

  const { data: tenantsData } = useSWR<{ data: TenantOption[] }>(
    '/admin/tenants',
    (url: string) => apiClient.get(url).then(r => r.data),
    { revalidateOnFocus: false },
  );

  const tenants = tenantsData?.data ?? [];

  const rows = (data?.data ?? []).filter(u => {
    const matchSearch = !search ||
      u.nombre.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.tenant_nombre.toLowerCase().includes(search.toLowerCase());
    const matchRol    = rolFilt === 'all'    || u.rol === rolFilt;
    const matchTenant = tenantFilt === 'all' || u.tenant_id === tenantFilt;
    return matchSearch && matchRol && matchTenant;
  });

  // Tenants únicos presentes en los resultados para el filtro
  const tenantGroups = Array.from(new Set(rows.map(r => r.tenant_nombre))).sort();

  function openCreate() {
    setModal({ mode: 'create' });
  }

  function openEdit(user: AdminUserRow) {
    setModal({ mode: 'edit', user });
  }

  const btnIcon: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
    background: 'transparent', cursor: 'pointer', color: C.muted,
  };

  return (
    <div style={{ color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: C.text, margin: 0 }}>Usuarios</h1>
          <p style={{ fontSize: '0.8125rem', color: C.muted, margin: '4px 0 0' }}>
            Todos los usuarios de todos los tenants. Gestion completa CRUD.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => mutate()} title="Recargar" style={{ ...btnIcon }}>
            <RefreshCw size={14} />
          </button>
          <button
            onClick={openCreate}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.45rem 1rem', borderRadius: 6, border: 'none', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, }}
          >
            <UserPlus size={14} /> Nuevo usuario
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Buscar nombre, email o empresa..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 280 }}
        />
        <select value={rolFilt} onChange={e => setRolFilt(e.target.value as typeof rolFilt)} style={{ ...inputStyle, width: 'auto' }}>
          <option value="all">Todos los roles</option>
          <option value="admin">Admin</option>
          <option value="user">Usuario</option>
          <option value="employee">Empleado</option>
        </select>
        <select value={tenantFilt} onChange={e => setTenantFilt(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
          <option value="all">Todos los tenants</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.nombre_comercial}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: C.muted }}>
          {isLoading ? 'Cargando...' : `${rows.length} usuario${rows.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Tabla agrupada */}
      {isLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: C.muted, fontSize: '0.875rem' }}>Cargando usuarios...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: C.muted, fontSize: '0.875rem' }}>No se encontraron usuarios.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {tenantGroups.map(tenantNombre => {
            const tenantRows = rows.filter(r => r.tenant_nombre === tenantNombre);
            const slug = tenantRows[0]?.tenant_slug ?? '';
            return (
              <div key={tenantNombre}>
                {/* Cabecera grupo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{tenantNombre}</span>
                  <span style={{ fontSize: '0.75rem', color: C.muted, }}>/{slug}</span>
                  <span style={{ fontSize: '0.75rem', color: C.muted }}>— {tenantRows.length} usuario{tenantRows.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Tabla */}
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                        {['Nombre', 'Email', 'Rol', 'Estado', 'Desde', 'Acciones'].map(h => (
                          <th key={h} style={{ padding: '0.5rem 1rem', textAlign: h === 'Acciones' ? 'right' : 'left', fontSize: '0.6875rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tenantRows.map((user, i) => {
                        const badge = ROL_BADGE[user.rol];
                        return (
                          <tr
                            key={user.id}
                            style={{
                              borderBottom: i < tenantRows.length - 1 ? `1px solid ${C.border}` : 'none',
                              background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                              opacity: user.activo ? 1 : 0.45,
                            }}
                          >
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: C.text, fontWeight: 500 }}>
                              {user.nombre}
                              {user.employee_nombre && (
                                <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: 1 }}>
                                  {user.employee_nombre} {user.employee_apellidos ?? ''}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: C.muted }}>
                              {user.email}
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: badge.bg, color: badge.color }}>
                                {badge.label}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                                background: user.activo ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.1)',
                                color: user.activo ? '#4ade80' : '#f87171',
                              }}>
                                {user.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: C.muted, whiteSpace: 'nowrap' }}>
                              {formatDate(user.created_at)}
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => openEdit(user)}
                                  title="Editar usuario"
                                  style={{ ...btnIcon }}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  onClick={() => setToDelete(user)}
                                  title="Desactivar usuario"
                                  style={{ ...btnIcon, color: '#f87171', borderColor: 'rgba(220,38,38,0.3)' }}
                                  disabled={!user.activo}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear / editar */}
      {modal && (
        <UserModal
          mode={modal.mode}
          tenants={tenants}
          initial={modal.user
            ? {
                id:        modal.user.id,
                tenant_id: modal.user.tenant_id,
                nombre:    modal.user.nombre,
                email:     modal.user.email,
                password:  '',
                rol:       modal.user.rol,
                activo:    modal.user.activo,
              }
            : EMPTY_FORM
          }
          onClose={() => setModal(null)}
          onSaved={() => mutate()}
        />
      )}

      {/* Modal eliminar */}
      {toDelete && (
        <DeleteModal
          user={toDelete}
          onClose={() => setToDelete(null)}
          onDeleted={() => mutate()}
        />
      )}
    </div>
  );
}
